import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Zap, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { logError } from "../lib/logger";

const IMPACT_DOT = { High: "bg-red-500", Medium: "bg-amber-500", Low: "bg-zinc-500" };
const CUR_BADGE = { USD: "bg-blue-500/15 text-blue-300", EUR: "bg-emerald-500/15 text-emerald-300" };
const SRC_LABEL = { gemini: "AI", manual: "Manuale", fred: "FRED" };

const ActualState = ({ ev }) => {
  if (ev.actual) {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-emerald-400" data-testid="nfp-actual-value">
        {ev.actual}
        {ev.actual_src ? <span className="text-[10px] px-1 py-0.5 rounded-sm bg-white/5 text-zinc-500 font-sans">{SRC_LABEL[ev.actual_src] || ev.actual_src}</span> : null}
      </span>
    );
  }
  if (ev.released) return <span className="text-amber-400 italic text-xs">In corso…</span>;
  return <span className="text-zinc-600">—</span>;
};

const EventRow = ({ ev, onFetch, onSave, busyKey }) => {
  const [val, setVal] = useState("");
  const busy = busyKey === ev.key;
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="nfp-event-row">
      <td className="p-2.5 text-xs text-zinc-500 font-mono whitespace-nowrap">{ev.date} · {ev.time || "—"}</td>
      <td className="p-2.5">
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm mr-2 ${CUR_BADGE[ev.currency] || "bg-white/10"}`}>{ev.currency}</span>
        <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${IMPACT_DOT[ev.impact] || "bg-zinc-600"}`} title={ev.impact} />
        <span className="text-sm text-zinc-200">{ev.title}</span>
      </td>
      <td className="p-2.5 text-xs text-zinc-400 font-mono whitespace-nowrap">P:{ev.forecast || "—"} · Pr:{ev.previous || "—"}</td>
      <td className="p-2.5 whitespace-nowrap"><ActualState ev={ev} /></td>
      <td className="p-2.5">
        <div className="flex items-center gap-1.5 justify-end">
          <input
            data-testid={`nfp-manual-input-${ev.key}`}
            value={val} onChange={(e) => setVal(e.target.value)}
            placeholder="es. 3.2%"
            className="w-20 px-2 py-1 rounded-sm bg-black/30 border border-white/10 text-xs text-white focus:border-emerald-500/50 outline-none"
          />
          <button
            data-testid={`nfp-save-btn-${ev.key}`}
            onClick={() => { onSave(ev.key, val); setVal(""); }}
            disabled={busy || !val.trim()}
            className="p-1.5 rounded-sm bg-emerald-600/80 text-white hover:bg-emerald-500 disabled:opacity-40"
            title="Salva dato inserito a mano (pubblico per tutti)">
            <Check size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default function AdminNFP() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/nfp/events");
      setEvents(r.data.events || []);
    } catch (err) { logError(err); toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fetchOne = async (key) => {
    setBusyKey(key);
    try {
      const r = await api.post("/admin/nfp/fetch", { key });
      if (r.data.ok) { toast.success(`Dato trovato: ${r.data.actual}`); load(); }
      else toast.warning(r.data.detail || "Dato non trovato");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  const saveOne = async (key, actual) => {
    if (!actual.trim()) return;
    setBusyKey(key);
    try {
      await api.post("/admin/nfp/set", { key, actual });
      toast.success("Dato fissato e reso pubblico");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  const fetchBatch = async () => {
    setBatchBusy(true);
    try {
      const r = await api.post("/admin/nfp/fetch-batch");
      toast.success(`Ricerca AI completata: ${r.data.filled} dati trovati`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBatchBusy(false); }
  };

  const pending = events.filter((e) => e.released && !e.actual).length;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-nfp-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft size={16} /> Torna al pannello Admin
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">NFP · Dati reali</h1>
          <p className="text-sm text-zinc-400">Inserisci i dati reali a mano: diventano pubblici per tutti gli utenti. <span className="text-amber-400">{pending} in attesa</span>.</p>
        </div>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-md overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="p-2.5 font-medium">Quando</th>
              <th className="p-2.5 font-medium">Evento</th>
              <th className="p-2.5 font-medium">Prev/Prec</th>
              <th className="p-2.5 font-medium">Reale</th>
              <th className="p-2.5 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Caricamento…</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Nessun evento.</td></tr>
            ) : events.map((ev) => (
              <EventRow key={ev.key} ev={ev} onFetch={fetchOne} onSave={saveOne} busyKey={busyKey} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
